import localeFromLib from 'locale2';
import { invert } from 'lodash';
import {
  getFeatureFlagOverrides,
  getOS,
  getOSVersion,
  strToArray
} from 'config-utils';
import { OsPermissionStatus } from './constants';
import defaultTranslations from 'translations/en-US.json';

// device placeholders and active&premium icons (bezels)
import emptySlotIonic from 'inline-icons/empty-slot-ionic-v2.svg';
import emptySlotVersa from 'inline-icons/empty-slot-versa-v2.svg';
import emptySlotCharge from 'inline-icons/empty-slot-charge-v2.svg';
import emptySlotInspire from 'inline-icons/empty-slot-inspire-v2.svg';
import emptySlotSense from 'inline-icons/empty-slot-sense-v2.svg';
import emptySlotLuxe from 'inline-icons/empty-slot-luxe.svg';
import emptySlotMorgan from 'inline-icons/empty-slot-morgan.svg';

import activeClockIconIonic from 'icons/active-clock-icon-ionic.svg';
import activeClockIconVersa from 'icons/active-clock-icon-versa.svg';
import activeClockIconCharge from 'icons/active-clock-icon-charge.svg';
import activeClockIconInspire from 'icons/active-clock-icon-inspire.svg';
import activeClockIconSense from 'icons/active-clock-icon-sense.svg';
import activeClockIconLuxe from 'icons/active-clock-icon-luxe.svg';
import activeClockIconMorgan from 'icons/active-clock-icon-morgan.svg';

import premiumClockIconIonic from 'icons/premium-clock-icon-ionic.svg';
import premiumClockIconVersa from 'icons/premium-clock-icon-versa.svg';
import premiumClockIconCharge from 'icons/premium-clock-icon-charge.svg';
import premiumClockIconInspire from 'icons/premium-clock-icon-inspire.svg';
import premiumClockIconSense from 'icons/premium-clock-icon-sense.svg';
import premiumClockIconLuxe from 'icons/premium-clock-icon-luxe.svg';
import premiumClockIconMorgan from 'icons/premium-clock-icon-morgan.svg';
import premiumTileIconHera from 'icons/premium-tile-icon-hera.svg';
import premiumClockIconMelroy from 'icons/premium-clock-icon-melroy.svg';

import heraTileEmptySlot from 'icons/empty-slot-hera-tiles.svg';

export const fitbitApiUrl = process.env.REACT_APP_FITBIT_API_URL;
export const galleryApiUrl = `${fitbitApiUrl}/1/app-gallery`;
export const galleryGraphQlUrl = `${galleryApiUrl}/graphql`;
export const registryApiUrl = `${fitbitApiUrl}/1/app-registry`;

// client OS
export const deviceEmulation = process.env.REACT_APP_DEVICE_EMULATION;
export const os = getOS({ override: deviceEmulation });
export const osVersion = getOSVersion();
export const isiOS = os === 'iOS';
export const isWindows = os === 'Windows';
export const isAndroid = os === 'Android';

// Defaults to 1 minute (60 seconds)
export const fetchInstalledInterval =
  parseInt(process.env.REACT_APP_FETCH_INSTALLED_INTERVAL, 10) || 60 * 1000;

// Defaults to 10 seconds
export const fetchDuringSyncInterval =
  parseInt(process.env.REACT_APP_FETCH_DURING_SYNC_INTERVAL, 10) || 10 * 1000;

// Defaults to 10 seconds
export const bannerAutoRotateInterval =
  parseInt(process.env.REACT_APP_BANNER_ROTATE_INTERVAL, 10) || 10 * 1000;

export const appsPerPage = 24;
export const clocksPerPage = 36;
export const clocksPerRow = 3;
export const viewAllLimit = 3;

/* page layout */
export const pageLayout = {
  APP: {
    numPreviewApps: 6
  },
  CLOCK: {
    numPreviewApps: 4
  },
  TILE: {
    numPreviewApps: 4
  }
};

/* feature flags */
const VALID_FEATURE_FLAGS = {
  // Used to control when to show Higgs CU2 experiences
  showHiggsCU2: 'showHiggsCU2',
  // Control if gallery is gated to newer mobile clients for CU2
  enableGalleryGating: 'enableGalleryGating',
  // Used to control when to show search
  showSearch: 'showSearch',
  // Used to control if multiple clockfaces are allowed
  allowMultipleClockfaces: 'allowMultipleClockfaces',
  // Used to control if favorite clockfaces are enabled
  enableFavoriteClockfaces: 'enableFavoriteClockfaces',
  // Used to control if ratings are enabled
  enable_ratings: 'enable_ratings'
};
export const featureFlag = {
  valid: VALID_FEATURE_FLAGS,
  overrides: getFeatureFlagOverrides({
    valid: VALID_FEATURE_FLAGS,
    enable: strToArray(process.env.REACT_APP_ENABLED_FEATURES),
    disable: strToArray(process.env.REACT_APP_DISABLED_FEATURES)
  }),
  defaults: {
    showHiggsCU2: false,
    enableGalleryGating: true,
    allowMultipleClockfaces: false,
    enableFavoriteClockfaces: false,
    enable_ratings: false,
    enable_tiles: false,
    force_gallery_v1: false,
    force_gallery_v2: false
  }
};

/* Permissions */
export const enableableOsPermissionStatuses = [
  OsPermissionStatus.DENIED,
  OsPermissionStatus.UNDETERMINED
];

export const fitbitToOsPermissionMap = Object.freeze({
  access_location: 'location',
  access_calendar: 'calendar',
  mobile_notifications: 'notifications'
});

export const osToFitbitPermissionMap = invert(fitbitToOsPermissionMap);

/* i18n */
export { defaultTranslations };
export const defaultLocale = 'en-US';
export const locale = process.env.REACT_APP_LOCALE || localeFromLib;
export const supportedLocales = [
  'de-DE',
  'en-GB',
  'en-US',
  'es-ES',
  'es-LA',
  'fr-CA',
  'fr-FR',
  'it-IT',
  'ja-JP',
  'ko-KR',
  'nl-NL',
  'sv-SE',
  'zh-CN',
  'zh-TW',
  'cs-CZ',
  'id-ID',
  'pl-PL',
  'pt-BR',
  'ro-RO',
  'ru-RU',
  'nb-NO'
];
// Map of language codes to a fallback locale (e.g. es-MX will fall back to es-ES)
export const languageToLocaleMap = {
  cs: 'cs-CZ',
  de: 'de-DE',
  en: 'en-US',
  es: 'es-ES',
  fr: 'fr-FR',
  id: 'id-ID',
  it: 'it-IT',
  ja: 'ja-JP',
  ko: 'ko-KR',
  nl: 'nl-NL',
  pl: 'pl-PL',
  pt: 'pt-BR',
  ro: 'ro-RO',
  ru: 'ru-RU',
  sv: 'sv-SE',
  zh: 'zh-CN',
  no: 'nb-NO'
};

/* errors */
export const defaultError = 'unknown';

const commonCommsEventErrors = [
  'connectionunexpectedlyterminated',
  'connecttointernetfailure',
  'deviceconnectionerror',
  'hwfailure',
  'joinfailure',
  'nocontentavailable',
  'sitesyncfailure',
  'tlshandshakefailure',
  'unknown',
  'unsupportedauthtype',
  'wificonnectionerror',
  'wificonnectionissue' /* Deprecated, used by pre-2.44/2.63 mobile clients */,
  'wifiinitfailure',
  'wifilowbattery',
  'wifinotsetup'
];

// Simple map of comms event names to Sets of supported errors
export const commsEventSupportedErrors = {
  'wifi-connected-status': new Set([...commonCommsEventErrors]),
  'app-install-completion': new Set([
    'aisloopdetected',
    ...commonCommsEventErrors
  ]),
  'device-connection-status': new Set(['disconnected']),
  'wifi-connect': new Set([...commonCommsEventErrors])
};

// Hard-coded app and clock recs
export const APP_RECOMMENDATIONS = ['Golf', 'Weather', 'Exercise', 'Music'];
export const CLOCK_RECOMMENDATIONS = [
  'Analog',
  'Digital',
  'Classic',
  'Branded'
];

// device placeholders for the facorites clocks screen
export const DEVICE_PLACEHOLDERS = {
  ionic: {
    emptySlot: emptySlotIonic,
    activeClockIcon: activeClockIconIonic,
    premiumClockIcon: premiumClockIconIonic
  },
  versa: {
    emptySlot: emptySlotVersa,
    activeClockIcon: activeClockIconVersa,
    premiumClockIcon: premiumClockIconVersa
  },
  'versa lite': {
    emptySlot: emptySlotVersa,
    activeClockIcon: activeClockIconVersa,
    premiumClockIcon: premiumClockIconVersa
  },
  'versa 2': {
    emptySlot: emptySlotVersa,
    activeClockIcon: activeClockIconVersa,
    premiumClockIcon: premiumClockIconVersa
  },
  'versa 3': {
    emptySlot: emptySlotSense,
    activeClockIcon: activeClockIconSense,
    premiumClockIcon: premiumClockIconSense
  },
  'charge 3': {
    emptySlot: emptySlotCharge,
    activeClockIcon: activeClockIconCharge,
    premiumClockIcon: premiumClockIconCharge
  },
  'charge 4': {
    emptySlot: emptySlotCharge,
    activeClockIcon: activeClockIconCharge,
    premiumClockIcon: premiumClockIconCharge
  },
  sense: {
    emptySlot: emptySlotSense,
    activeClockIcon: activeClockIconSense,
    premiumClockIcon: premiumClockIconSense
  },
  inspire: {
    emptySlot: emptySlotInspire,
    activeClockIcon: activeClockIconInspire,
    premiumClockIcon: premiumClockIconInspire
  },
  'inspire 2': {
    emptySlot: emptySlotInspire,
    activeClockIcon: activeClockIconInspire,
    premiumClockIcon: premiumClockIconInspire
  },
  luxe: {
    emptySlot: emptySlotLuxe,
    activeClockIcon: activeClockIconLuxe,
    premiumClockIcon: premiumClockIconLuxe
  },
  'charge 5': {
    emptySlot: emptySlotMorgan,
    activeClockIcon: activeClockIconMorgan,
    premiumClockIcon: premiumClockIconMorgan
  },
  'inspire 3': {
    emptySlot: emptySlotLuxe,
    activeClockIcon: activeClockIconLuxe,
    premiumClockIcon: premiumClockIconLuxe
  },
  'versa 4': {
    emptySlot: emptySlotSense,
    activeClockIcon: activeClockIconSense,
    premiumClockIcon: premiumClockIconSense,
    premiumTileIcon: premiumTileIconHera,
    tileEmptySlot: heraTileEmptySlot
  },
  'sense 2': {
    emptySlot: emptySlotSense,
    activeClockIcon: activeClockIconSense,
    premiumClockIcon: premiumClockIconSense,
    premiumTileIcon: premiumTileIconHera,
    tileEmptySlot: heraTileEmptySlot
  },
  'charge 6': {
    emptySlot: emptySlotMorgan,
    activeClockIcon: activeClockIconMorgan,
    premiumClockIcon: premiumClockIconMelroy
  }
};

// sizes and margins for the device preview image
export const DEVICE_PREVIEW_DIMENSIONS = {
  ionic: {
    top: 137,
    width: 173,
    translateLeft: -0.3,
    frameHeight: 422,
    marginTop: -112
  },
  versa: {
    top: 100,
    width: 147,
    translateLeft: -0.3,
    frameHeight: 380,
    marginTop: -90
  },
  'versa lite': {
    top: 100,
    width: 150,
    translateLeft: 0,
    frameHeight: 380,
    marginTop: -90
  },
  'versa 2': {
    top: 107,
    width: 150,
    translateLeft: -1,
    frameHeight: 380,
    marginTop: -90
  },
  'versa 3': {
    top: 118,
    width: 180,
    translateLeft: 0,
    frameHeight: 410,
    marginTop: -90
  },
  'versa 4': {
    top: 114,
    width: 180,
    translateLeft: -2,
    frameHeight: 410,
    marginTop: -90
  },
  'charge 3': {
    top: 118,
    width: 85,
    frameHeight: 420,
    marginTop: -110
  },
  'charge 4': {
    top: 118,
    width: 85,
    frameHeight: 420,
    marginTop: -110
  },
  inspire: {
    top: 134,
    width: 72,
    translateLeft: -3.5,
    frameHeight: 445,
    marginTop: -120
  },
  'ace 2': {
    top: 108,
    width: 72,
    translateLeft: 0,
    frameHeight: 398,
    marginTop: -100
  },
  sense: {
    top: 118,
    width: 180,
    translateLeft: 0,
    frameHeight: 410,
    marginTop: -90
  },
  'sense 2': {
    top: 114,
    width: 180,
    translateLeft: -2,
    frameHeight: 410,
    marginTop: -90
  },
  'inspire 2': {
    top: 140,
    width: 72,
    translateLeft: 0,
    frameHeight: 410,
    marginTop: -90
  },
  'ace 3': {
    top: 140,
    width: 72,
    translateLeft: 0,
    frameHeight: 410,
    marginTop: -90
  },
  luxe: {
    top: 135,
    width: 82,
    translateLeft: 0,
    frameHeight: 410,
    marginTop: -90
  },
  'charge 5': {
    top: 131,
    width: 96,
    frameHeight: 400,
    translateLeft: 0.1,
    marginTop: -90,
    borderRadius: 12
  },
  'inspire 3': {
    top: 135,
    width: 82,
    translateLeft: 0,
    frameHeight: 410,
    marginTop: -90
  },
  'charge 6': {
    top: 127,
    width: 96,
    frameHeight: 400,
    translateLeft: 0.1,
    marginTop: -90,
    borderRadius: 12
  }
};

// internal device renames
export const DEVICE_RENAME = {
  'inspire hr': 'inspire'
};

// device renames for the Device tab button
export const TOP_BAR_DEVICE_RENAME = {
  'Ace — for kids!': 'Ace',
  'Ace 2 — for kids!': 'Ace 2',
  'Ace 3 — for kids!': 'Ace 3'
};

export const sentry = {
  dsn:
    process.env.SENTRY_DSN ||
    'https://9cd03bf95539429e8a9fbba164b44e5a@o83246.ingest.sentry.io/5764537',
  tracesSampleRate: parseInt(process.env.SENTRY_TRACE_SAMPLE_RATE, 10) || 0.5,
  sampleRate: parseInt(process.env.SENTRY_SAMPLE_RATE, 10) || 0.5
};

export const hidenCollectionsIds = [
  'cc996adf-9d58-4a39-a00f-2f6f81352495',
  '4738a548-bdf6-494b-9895-bcf32d149d96'
];
